import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskLine, Eyebrow } from "./motion";

export default function Hero({ profile }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const tagline = profile?.tagline || "Curating the Fine Art of Living";
  const words = tagline.split(" ");
  const mid = Math.ceil(words.length / 2);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen bg-[#FAF9F6] overflow-hidden pt-32 pb-16">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 relative z-10">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <Eyebrow>Cayman Islands · Luxury Real Estate</Eyebrow>
          </motion.div>

          <h1 className="font-serif text-[3.25rem] sm:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight text-[#0A1128] mt-6">
            <MaskLine delay={0.35}>{words.slice(0, mid).join(" ")}</MaskLine>
            <MaskLine delay={0.5}><span className="italic text-[var(--gold)]">{words.slice(mid).join(" ")}</span></MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-8 text-lg text-[var(--slate)] max-w-md leading-relaxed"
          >
            A private practice devoted to the Cayman Islands&apos; most exceptional homes — guided by local insight, discretion, and an unwavering standard.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button data-testid="hero-view-listings" onClick={() => scrollTo("listings")}
              className="px-8 py-4 bg-[#0A1128] text-[#FAF9F6] text-sm uppercase tracking-[0.14em] hover:bg-[#1a2342] transition-colors duration-300">
              View Collection
            </button>
            <button data-testid="hero-contact" onClick={() => scrollTo("contact")}
              className="px-8 py-4 border border-[#0A1128] text-[#0A1128] text-sm uppercase tracking-[0.14em] hover:bg-[#0A1128] hover:text-[#FAF9F6] transition-colors duration-300">
              Arrange a Consultation
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 1 }}
            className="mt-16 flex gap-10 border-t border-[#0A1128]/10 pt-8"
          >
            {[
              [`${profile?.years_experience || 23}+`, "Years"],
              [`${profile?.properties_sold || 100}+`, "Sales"],
              [profile?.total_value || "US$40M", "In Sales"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-serif text-3xl text-[#0A1128]">{n}</div>
                <div className="text-xs uppercase tracking-[0.16em] text-[var(--slate)] mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-6 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ delay: 0.4, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden"
          >
            <motion.img
              style={{ y: imgY }}
              src="https://customer-assets-agu9un31.emergentagent.net/job_property-showcase-572/artifacts/whz904r7_20250225_145004.webp"
              alt="Oceanfront living room with panoramic Caribbean view"
              className="w-full h-[68vh] object-cover scale-110"
            />
          </motion.div>
          {profile?.headshot && (
            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
              className="absolute -bottom-8 -left-6 md:-left-10 bg-[#FAF9F6] p-3 border border-[#0A1128]/10 shadow-sm"
            >
              <img src={profile.headshot} alt={profile?.name} className="w-36 h-44 md:w-44 md:h-56 object-cover object-top" />
              <p className="font-serif text-lg mt-2 text-[#0A1128] leading-tight">{profile?.name}</p>
              <p className="text-[10px] uppercase tracking-[0.16em] text-[var(--slate)]">Cayman Property Agent</p>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
